/**
 * Utility functions for handling product categories
 * Handles hidden categories and antibiotic detection
 */

// Categories that should be hidden from users (not shown in featured products or search)
// Hide: insectcide, vaccine, 0
const HIDDEN_CATEGORIES = ["insectcide", "vaccine", "0"];

/**
 * Check if a category or sub_category should be hidden
 * @param {string} category - Category name to check
 * @param {string} subCategory - Sub category name to check (optional)
 * @returns {boolean} - True if category or sub_category should be hidden
 */
export const isCategoryHidden = (category, subCategory = null) => {
  if (!category && !subCategory) return false;

  const checkValue = (value) => {
    if (!value) return false;
    return HIDDEN_CATEGORIES.some(
      (hiddenCat) => value.toLowerCase() === hiddenCat.toLowerCase()
    );
  };

  return checkValue(category) || checkValue(subCategory);
};

/**
 * Check if a product belongs to a hidden category
 * Checks both category and sub_category fields
 * @param {Object} product - Product object with category and sub_category fields
 * @returns {boolean} - True if product should be hidden
 */
export const isProductHidden = (product) => {
  if (!product) return false;
  return isCategoryHidden(product.category, product.sub_category);
};

/**
 * Filter out products with hidden categories
 * @param {Array} products - Array of products
 * @returns {Array} - Filtered products without hidden categories
 */
export const filterHiddenCategories = (products) => {
  if (!Array.isArray(products)) return [];
  return products.filter((product) => !isProductHidden(product));
};

/**
 * Check if a product is an antibiotic
 * Checks category, sub_category, and itemName for antibiotic-related terms
 * @param {Object} product - Product object
 * @returns {boolean} - True if product is an antibiotic
 */
export const isAntibiotic = (product) => {
  if (!product) return false;

  const antibioticTerms = ["antibiotic", "antibiotics"];
  const searchFields = [
    product.category,
    product.sub_category,
    product.itemName,
  ];

  return searchFields.some((field) => {
    if (!field) return false;
    const fieldLower = field.toLowerCase();
    return antibioticTerms.some((term) => fieldLower.includes(term));
  });
};

/**
 * Mapping of subcategories to their generic image paths
 * Handles variations and case-insensitive matching
 */
// Import all generic images from the public folder
const SUBCATEGORY_GENERIC_IMAGES = {
  // Normalized subcategory names to image paths
  antibiotic: "/generic-images/antibiotic.png",
  mouthwash: "/generic-images/mouthwash.png",
  antiseptic: "/generic-images/antiseptic.png",
  antifungal: "/generic-images/antifungal.png",
  probiotic: "/generic-images/probiotic.png",
  antihistamine: "/generic-images/antihistamine.png",
  multivitamin: "/generic-images/multivitamin.png",
  "iron supplement": "/generic-images/iron-supplement.png",
  "anti-diabetic": "/generic-images/anti-diabetic.png",
  antidiabetic: "/generic-images/anti-diabetic.png",
  immunosuppressant: "/generic-images/immunosuppressant.png",
  statin: "/generic-images/statin.png",
  "reductase inhibitor": "/generic-images/reductase-inhibitor.png",
  supplement: "/generic-images/supplement.png",
  analgesic: "/generic-images/analgesic.png",
  laxative: "/generic-images/laxative.png",
  "nerve medication": "/generic-images/nerve-medication.png",
  "calcium supplement": "/generic-images/calcium-medicine.png",
  "anti-hypertensive": "/generic-images/anti-hypertensive.png",
  antihypertensive: "/generic-images/anti-hypertensive.png",
  footcare: "/generic-images/footcare.png",
  "joint supplement": "/generic-images/joint-supplement.png",
  "digestive medication": "/generic-images/digestive-medication.png",
  "cough medicine": "/generic-images/cough-medicine.png",
  "anti-prostate": "/generic-images/anti-prostate.png",
  antacid: "/generic-images/antacid.png",
  "heart medication": "/generic-images/heart-medication.png",
  analgesics: "/generic-images/analgesic.png",
};

/**
 * Get generic image path for a subcategory
 * @param {string} subCategory - Sub category name
 * @returns {string|null} - Generic image path or null if not found
 */
const getGenericImageForSubcategory = (subCategory) => {
  if (!subCategory) return null;

  // Normalize subcategory name: lowercase, trim, and remove extra spaces
  const normalized = subCategory.toLowerCase().trim().replace(/\s+/g, " ");

  // First, try direct match
  if (SUBCATEGORY_GENERIC_IMAGES[normalized]) {
    console.log(
      `[getGenericImage] Direct match for "${subCategory}" -> ${SUBCATEGORY_GENERIC_IMAGES[normalized]}`
    );
    return SUBCATEGORY_GENERIC_IMAGES[normalized];
  }

  // Sort entries by key length (longest first) to prioritize more specific matches
  const sortedEntries = Object.entries(SUBCATEGORY_GENERIC_IMAGES).sort(
    (a, b) => b[0].length - a[0].length
  );

  // Try to find a match - prioritize longer/more specific keys
  for (const [key, imagePath] of sortedEntries) {
    const keyLower = key.toLowerCase();

    // Exact match (already handled above, but check again for completeness)
    if (normalized === keyLower) {
      console.log(
        `[getGenericImage] Exact match for "${subCategory}" -> ${imagePath}`
      );
      return imagePath;
    }

    // Check if normalized contains the full key as a phrase (for multi-word keys)
    if (keyLower.includes(" ") && normalized.includes(keyLower)) {
      console.log(
        `[getGenericImage] Phrase match for "${subCategory}" with key "${key}" -> ${imagePath}`
      );
      return imagePath;
    }

    // Check if key contains normalized (for when subcategory is shorter)
    if (keyLower.includes(normalized)) {
      console.log(
        `[getGenericImage] Contains match for "${subCategory}" with key "${key}" -> ${imagePath}`
      );
      return imagePath;
    }

    // Word-based matching: check if all words from key are in normalized (for single-word keys or when phrase doesn't match)
    const keyWords = keyLower.split(/\s+/).filter((w) => w.length > 0);
    if (keyWords.length > 0) {
      const allWordsMatch = keyWords.every((keyWord) =>
        normalized.includes(keyWord)
      );
      // Only use word matching if it's a single word or if we haven't found a better match
      if (
        allWordsMatch &&
        (keyWords.length === 1 || normalized.includes(keyLower))
      ) {
        console.log(
          `[getGenericImage] Word match for "${subCategory}" with key "${key}" -> ${imagePath}`
        );
        return imagePath;
      }
    }
  }

  console.log(`[getGenericImage] No match found for "${subCategory}"`);
  return null;
};

/**
 * Get image source for a product, hiding images for antibiotics
 * Uses generic subcategory image if product has no image
 * @param {Object} product - Product object
 * @param {string} defaultImage - Default image URL to use for antibiotics or fallback
 * @returns {string} - Image URL (or generic subcategory image, or default)
 */
export const getProductImage = (product, defaultImage) => {
  // If product has an image (check for truthy and non-empty string), use it
  // But ONLY if it's not an antibiotic
  if (
    !isAntibiotic(product) &&
    product.image &&
    typeof product.image === "string" &&
    product.image.trim() !== ""
  ) {
    return `https://dawamaxma.dawamtaani.co.ke${product.image}`;
  }

  // If no image (or it's an antibiotic), try to get generic image for subcategory
  if (product.sub_category) {
    const genericImage = getGenericImageForSubcategory(product.sub_category);
    if (genericImage) {
      console.log(
        `[getProductImage] Using generic image for subcategory "${product.sub_category}": ${genericImage}`
      );
      return genericImage;
    } else {
      console.log(
        `[getProductImage] No generic image found for subcategory: "${product.sub_category}"`
      );
    }
  } else {
    console.log(
      `[getProductImage] Product "${product.itemName}" has no sub_category`
    );
  }

  // Fallback to default image
  return defaultImage;
};

/**
 * Filter categories to exclude hidden ones
 * @param {Array} categories - Array of category names
 * @returns {Array} - Filtered categories without hidden ones
 */
export const filterHiddenCategoriesFromList = (categories) => {
  if (!Array.isArray(categories)) return [];
  return categories.filter((category) => !isCategoryHidden(category));
};
